import { z } from "zod";

export const metricWidgetItemValidator = z.object({
    id: z.string(),
    type: z.enum(["bar", "metric", "capacityMetric"]),
    label: z.string(),
    unit: z.string().optional(),
    chartHeaderText: z.string().optional(),
    hideSummary: z.boolean().optional(),
    barData: z.array(z.object({
        id: z.string(),
        label: z.string(),
        color: z.string(),
        legendTitle: z.string(),
        tooltip: z.string().optional(),
    })).optional()
})

export const widgetActionValidator = z.object({
    label: z.string(),
    to: z.string(),
    bluexpTo: z.string().optional(),
    variant: z.enum(["text"]).optional(),
    color: z.enum(["primary", "secondary"]).optional()
})


export const widgetStateValidator = z.object({
    showMetrics: z.boolean().default(true),
    description: z.string().optional(),
    actions: z.array(widgetActionValidator).optional(),
    showLearnMore: z.boolean().optional()
})

export const widgetValidator = z.object({
    title: z.string(),
    primaryCtaTo: z.string(),
    primaryCtaBluexpTo: z.string(),
    primaryCtaLabel: z.string(),
    noCredentialCtaDropdownTo: z.array(z.object({
        to: z.string(),
        bluexpTo: z.string().optional(),
        label: z.string(),
        isDisabled: z.boolean().optional(),
        disabledTooltip: z.string().optional()
    })).optional(),
    noCredentialsDescription: z.string().optional(),
    metricWidgets: z.array(metricWidgetItemValidator),
    noCredentialCtaTo: z.string().optional(),
    noCredentialCtaBluexpTo: z.string().optional(),
    noCredentialCtaLabel: z.string().optional(),
    dataUrl: z.string(),
    marketingUrl: z.string().optional(),
    marketingBlueXPUrl: z.string().optional(),
    featureId: z.string().optional(),
    states: z.record(z.string(), widgetStateValidator).optional(),
    icon: z.string().startsWith("<svg").prefault("<svg viewBox=\"0 0 24 24\" xmlns=\"http://www.w3.org/2000/svg\"><circle cx=\"12\" cy=\"12\" r=\"10\" style=\"stroke: rgb(0, 0, 0); fill: rgb(255, 255, 255);\"/></svg>")
}).optional()

export const focusValidator = z.array(z.object({
        id: z.string(),
        suffixPlural: z.string(),
        suffixSingular: z.string(),
        dataUrl: z.string(),
        primaryCtaTo: z.string(),
        primaryCtaBluexpTo: z.string(),
        primaryCtaLabel: z.string(),
        featureId: z.string().optional(),
        label: z.string().optional(),
        resourcesLabelSingular: z.string().optional(),
        resourcesLabelPlural: z.string().optional()
    })).optional()

export const tabHideIfEnum = z.enum(["isGovAccount", "isDemoAccount"]);
export type TabHideIf = z.infer<typeof tabHideIfEnum>;

export const workloadValidator = z.object({
    name: z.string(),
    id: z.string(),
    to: z.string().startsWith("/"),
    bluexpTo: z.string().optional(),
    logo: z.string().startsWith("<svg").prefault("<svg viewBox=\"157 120.502 104 104\" xmlns=\"http://www.w3.org/2000/svg\"><rect x=\"157\" y=\"120.502\" width=\"104\" height=\"104\" style=\"stroke: rgb(0, 0, 0); fill: rgb(6, 89, 247);\"/></svg>"),
    hasPolicies: z.boolean().optional(),
    hasTracker: z.boolean().optional(),
    hasMenuItem: z.boolean().optional(),
    apiDocUrl: z.string().optional().describe("relative path starting after /accounts/:id"),
    stageIntegrations: z.array(z.object({
        path: z.string().startsWith("/"),
        renderType: z.enum(["iframe/v2", "webcomponent/v1"]),
        iframeUrl: z.string().optional(),
        url: z.string().optional()
    })).optional(),
    homepage: z.object({
        description: z.string(),
        isComingSoon: z.boolean().optional(),
        dataUrl: z.string(),
        primaryCtaDropdownTo: z.array(z.object({
            to: z.string(),
            bluexpTo: z.string().optional(),
            label: z.string(),
            isDisabled: z.boolean().optional(),
            disabledTooltip: z.string().optional()
        })).optional(),
        primaryCtaDropdownLabel: z.string().optional(),
        primaryCtaTo: z.string().optional(),
        primaryCtaBluexpTo: z.string().optional(),
        primaryCtaLabel: z.string().optional(),
        secondaryCtaTo: z.string().optional(),
        secondaryCtaBluexpTo: z.string().optional(),
        secondaryCtaLabel: z.string().optional(),
        mainPrimaryCtaDropdownTo: z.array(z.object({
            to: z.string(),
            bluexpTo: z.string().optional(),
            label: z.string(),
            isDisabled: z.boolean().optional(),
            disabledTooltip: z.string().optional()
        })).optional(),
        mainPrimaryCtaDropdownLabel: z.string().optional(),
        featureId: z.string().optional(),
        marketingUrl: z.string().optional(),
        marketingBlueXPUrl: z.string().optional(),
    })
        .optional()
        .refine((value) => {
                if(!value || value.isComingSoon){
                    return true;
                }
                //if primaryCtaDropdownTo only is present
                if((value.primaryCtaDropdownTo?.length || 0) > 0 && (value.mainPrimaryCtaDropdownTo?.length || 0) === 0 && !value.primaryCtaTo){
                    return true
                }
                //if mainPrimaryCtaDropdownTo only is present
                if((value.mainPrimaryCtaDropdownTo?.length || 0) > 0 && !value.primaryCtaTo && (value.primaryCtaDropdownTo?.length || 0) === 0){
                    return false;
                }
                //if every To has a corresponding label
                if((!!value.primaryCtaLabel !== !!value.primaryCtaTo) || ((value.primaryCtaDropdownTo?.length || 0) > 0 !== !!value.primaryCtaDropdownLabel) || ((value.mainPrimaryCtaDropdownTo?.length || 0) > 0 !== !!value.mainPrimaryCtaDropdownLabel)) {
                    return false;
                }
                //if primaryCtaTo is not together with mainPrimaryCtaDropdownTo (XOR)
                return !!value.primaryCtaTo !== (value.mainPrimaryCtaDropdownTo?.length || 0) > 0
            }
        ),
    nav: z.object({
        isComingSoon: z.boolean().optional()
    }).optional(),
    serviceNav: z.object({
        label: z.string(),
        description: z.string(),
        hideFromMenu: z.string().optional(),
        featureId: z.string().optional(),
        tabs: z.array(z.object({
            label: z.string(),
            to: z.string(),
            featureId: z.string().optional(),
            hideIf: z.array(tabHideIfEnum).optional(),
        })).optional(),
        ignoreChildPaths: z.array(z.string().startsWith("/")).optional(),
        isChildPath: z.boolean().optional(),
    }).optional(),
    features: z.array(z.object({
        title: z.string(),
        id: z.string(),
        isAnon: z.boolean().optional(),
        isAlwaysOn: z.boolean().optional(),
    })).optional(),
    focus: focusValidator,
    widget: widgetValidator
});

export const trackerValidator = z.object({
    name: z.string(),
    label: z.string().optional(),
    bluexpPrefix: z.string().optional(),
    actions: z.array(z.object({
        logo: z.string().optional(),
        displayName: z.string().optional(),
        name: z.string()
    })).optional()
});

export type WorkloadConfig = z.infer<typeof workloadValidator>;
export type WorkloadTracker = z.infer<typeof trackerValidator>;
export type WorkloadMetricWidget = z.infer<typeof metricWidgetItemValidator>;
export type WorkloadWidgetState = z.infer<typeof widgetStateValidator>;
export type WorkloadWidgetAction = z.infer<typeof widgetActionValidator>;

export const validate = (config: any) => {
    return workloadValidator.safeParse(config);
}
